import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { useFamilyTheme } from '../context/FamilyThemeContext';
import { 
  FaUsers, FaCalendarAlt, FaPlus, 
  FaCrown, FaArrowRight,
  FaEdit, FaArchive, FaUndo, FaTrash, FaEye, FaEyeSlash
} from 'react-icons/fa';
import { UserPlus, Users2 } from 'lucide-react';
import Header from '../components/Header';
import { Tutorial } from '../components/OnboardingTutorial';
import InviteProfessionalModal from '../components/Modals/InviteProfessionalModal';
import ReferFamilyModal from '../components/Modals/ReferFamilyModal';
import { RequestProButton } from '../components/professionals/RequestProButton';
import { IncentivesSummary } from '../components/IncentivesSummary';
import { FamilyPilotCard } from '../components/pilot/FamilyPilotCard';
import confetti from 'canvas-confetti';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const WELCOME_BANNER = 'https://static.prod-images.emergentagent.com/jobs/6e8c71c9-5deb-44de-959d-34c14877fd56/images/9f9dcc07134668bb8164f0a7364d481c32ed31172b976dd370521cbc2a50c91f.jpeg';

// Celebration confetti effect
const celebrateNewFamily = () => {
  const duration = 5000; // Extended from 3000ms to 5000ms for longer celebration
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  const interval = setInterval(function() {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    const particleCount = 50 * (timeLeft / duration);

    // Confetti from both sides
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      colors: ['#4F46E5', '#7C3AED', '#2563EB', '#10B981', '#F59E0B']
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      colors: ['#4F46E5', '#7C3AED', '#2563EB', '#10B981', '#F59E0B']
    });
  }, 250);
};

function Dashboard() {
  const { user } = useAuth();
  const { ancestralColors } = useFamilyTheme();
  const navigate = useNavigate();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [familyDescription, setFamilyDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  
  // Edit family state
  const [showEditFamily, setShowEditFamily] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Archive confirmation state
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archivingFamily, setArchivingFamily] = useState(null);
  
  // Show archived families toggle
  const [showArchived, setShowArchived] = useState(false);
  
  // Permanent delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingFamily, setDeletingFamily] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Onboarding state
  const [runTutorial, setRunTutorial] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  
  // Invite Professional modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Refer Family modal
  const [showReferModal, setShowReferModal] = useState(false);

  useEffect(() => {
    fetchFamilies(showArchived);
    fetchOnboarding();
  }, [showArchived]);

  // Listen for openReferFamily event from IncentivesSummary
  useEffect(() => {
    const handler = () => setShowReferModal(true);
    window.addEventListener('openReferFamily', handler);
    return () => window.removeEventListener('openReferFamily', handler);
  }, []);

  const fetchFamilies = async (includeArchived = false) => {
    try {
      const response = await axios.get(`${API}/families?include_archived=${includeArchived}`, {
        withCredentials: true
      });
      setFamilies(response.data);
    } catch (error) {
      console.error('Error fetching families:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/onboarding`, { withCredentials: true });
      setOnboarding(response.data);
      // Show tutorial for new users
      if (response.data.show_tutorial && response.data.completed_steps?.length === 0) {
        setRunTutorial(true);
      }
    } catch (error) {
      console.error('Error fetching onboarding:', error);
    }
  };

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(
        `${API}/families`,
        {
          name: familyName,
          description: familyDescription
        },
        { withCredentials: true }
      );

      setFamilies([...families, response.data]);
      setShowCreateFamily(false);
      setNewFamilyName(familyName);
      setFamilyName('');
      setFamilyDescription('');
      
      // Celebrate the new family! 🎉
      celebrateNewFamily();
      setShowCelebration(true);
      
      // Hide celebration after 5 seconds
      setTimeout(() => setShowCelebration(false), 5000);
      
      // Mark onboarding step as complete
      await axios.post(`${API}/onboarding/complete-step?step=create_family`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Error creating family:', error);
      alert('Failed to create family');
    } finally {
      setCreating(false);
    }
  };
  
  const handleEditFamily = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${API}/families/${editingFamily.family_id}`,
        {
          name: editName,
          description: editDescription
        },
        { withCredentials: true }
      );
      
      setFamilies(families.map(f => 
        f.family_id === editingFamily.family_id 
          ? { ...f, name: editName, description: editDescription }
          : f
      ));
      setShowEditFamily(false);
      setEditingFamily(null);
    } catch (error) {
      console.error('Error updating family:', error);
      alert('Failed to update family');
    }
  };
  
  const handleArchiveFamily = async () => {
    try {
      await axios.post(
        `${API}/families/${archivingFamily.family_id}/archive`,
        {},
        { withCredentials: true }
      );
      
      setFamilies(families.filter(f => f.family_id !== archivingFamily.family_id));
      setShowArchiveConfirm(false);
      setArchivingFamily(null);
    } catch (error) {
      console.error('Error archiving family:', error);
      alert('Failed to archive family');
    }
  };
  
  const handleRestoreFamily = async (family, e) => {
    e.stopPropagation();
    try {
      await axios.post(
        `${API}/families/${family.family_id}/restore`,
        {},
        { withCredentials: true }
      );
      
      // Update the family in state
      setFamilies(families.map(f => 
        f.family_id === family.family_id 
          ? { ...f, is_archived: false, archived_at: null }
          : f
      ));
    } catch (error) {
      console.error('Error restoring family:', error);
      alert('Failed to restore family');
    }
  };
  
  const handleDeletePermanently = async () => {
    if (deleteConfirmText !== deletingFamily.name) {
      alert('Please type the family name exactly to confirm deletion');
      return;
    }
    
    try {
      await axios.delete(
        `${API}/families/${deletingFamily.family_id}`,
        { withCredentials: true }
      );
      
      setFamilies(families.filter(f => f.family_id !== deletingFamily.family_id));
      setShowDeleteConfirm(false);
      setDeletingFamily(null);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting family:', error);
      alert('Failed to delete family');
    }
  };
  
  const openDeleteConfirm = (family, e) => {
    e.stopPropagation();
    setDeletingFamily(family);
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };
  
  const openEditModal = (family, e) => {
    e.stopPropagation();
    setEditingFamily(family);
    setEditName(family.name);
    setEditDescription(family.description || '');
    setShowEditFamily(true);
  };
  
  const openArchiveConfirm = (family, e) => {
    e.stopPropagation();
    setArchivingFamily(family);
    setShowArchiveConfirm(true);
  };
  
  const dismissTutorial = async () => {
    try {
      await axios.post(`${API}/onboarding/dismiss-tutorial`, {}, { withCredentials: true });
      setRunTutorial(false);
    } catch (error) {
      console.error('Error dismissing tutorial:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your families...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard">
      {/* Header */}
      <Header variant="dashboard" />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome back, {user?.name}!</h2>
          <p className="text-gray-600 mb-5">Manage your families and reunions all in one place.</p>
          <div className="rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/5" data-testid="dashboard-welcome-banner">
            <img
              src={WELCOME_BANNER}
              alt="A multi-generational family gathered together and waving hello on a sunlit lawn"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>

        {/* Create Family Button and Show Archived Toggle */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowCreateFamily(true)}
              className="flex items-center space-x-2 px-6 py-3 text-white rounded-lg hover:opacity-90 font-semibold shadow-md transition-all hover:shadow-lg"
              style={ancestralColors ? {
                background: `linear-gradient(135deg, ${ancestralColors.primary} 0%, ${ancestralColors.secondary} 100%)`
              } : {
                backgroundColor: '#2563EB'
              }}
              data-testid="create-family-btn"
            >
              <FaPlus />
              <span>Create New Family</span>
            </button>
            
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 font-semibold shadow-md transition-all hover:shadow-lg"
              data-testid="invite-professional-btn"
            >
              <UserPlus size={18} />
              <span>Invite a Professional</span>
            </button>
            
            <button
              onClick={() => setShowReferModal(true)}
              className="flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg hover:from-teal-600 hover:to-emerald-700 font-semibold shadow-md transition-all hover:shadow-lg"
              data-testid="refer-family-btn"
            >
              <Users2 size={18} />
              <span>Refer a Family</span>
            </button>

            <RequestProButton
              testId="dashboard-request-pro-btn"
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 font-semibold shadow-md transition-shadow hover:shadow-lg"
            />
          </div>
          
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition ${
              showArchived 
                ? 'bg-amber-100 text-amber-700 border border-amber-300' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="toggle-archived-btn"
          >
            {showArchived ? <FaEye /> : <FaEyeSlash />}
            <span>{showArchived ? 'Showing Archived' : 'Show Archived'}</span>
          </button>
        </div>

        {/* Families Grid */}
        <FamilyPilotCard />
        {families.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center" data-testid="no-families-message">
            <FaUsers className="text-gray-300 text-6xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {showArchived ? 'No archived families' : 'No families yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {showArchived 
                ? 'You have no archived families. Archived families will appear here.'
                : 'Create your first family to start planning reunions and building your legacy.'
              }
            </p>
            {!showArchived && (
              <button
                onClick={() => setShowCreateFamily(true)}
                className="px-6 py-3 text-white rounded-lg hover:opacity-90 font-semibold transition-all"
                style={ancestralColors ? {
                  background: `linear-gradient(135deg, ${ancestralColors.primary} 0%, ${ancestralColors.secondary} 100%)`
                } : {
                  backgroundColor: '#2563EB'
                }}
              >
                Create Your First Family
              </button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="families-grid">
            {families.map((family) => (
              <div
                key={family.family_id}
                className={`rounded-xl shadow-md hover:shadow-xl transition p-6 cursor-pointer relative group ${
                  family.is_archived 
                    ? 'bg-gray-100 border-2 border-dashed border-gray-300' 
                    : 'bg-white'
                }`}
                onClick={() => !family.is_archived && navigate(`/family/${family.family_id}`)}
                data-testid={`family-card-${family.family_id}`}
              >
                {/* Archived Badge */}
                {family.is_archived && (
                  <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    Archived
                  </div>
                )}
                
                {/* Actions for owner */}
                {family.created_by === user?.user_id && (
                  <div className="absolute top-4 right-4 flex space-x-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    {family.is_archived ? (
                      <>
                        {/* Restore button for archived families */}
                        <button
                          onClick={(e) => handleRestoreFamily(family, e)}
                          className="flex items-center gap-1 px-2 py-1.5 sm:p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 text-xs sm:text-sm"
                          title="Restore family"
                          data-testid={`restore-family-${family.family_id}`}
                        >
                          <FaUndo />
                          <span className="sm:hidden">Restore</span>
                        </button>
                        {/* Permanent delete button for archived families */}
                        <button
                          onClick={(e) => openDeleteConfirm(family, e)}
                          className="flex items-center gap-1 px-2 py-1.5 sm:p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-xs sm:text-sm"
                          title="Delete permanently"
                          data-testid={`delete-family-${family.family_id}`}
                        >
                          <FaTrash />
                          <span className="sm:hidden">Delete</span>
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Edit/Archive buttons for active families */}
                        <button
                          onClick={(e) => openEditModal(family, e)}
                          className="flex items-center gap-1 px-2 py-1.5 sm:p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-blue-100 hover:text-blue-600 text-xs sm:text-sm"
                          title="Edit family"
                          data-testid={`edit-family-${family.family_id}`}
                        >
                          <FaEdit />
                          <span className="sm:hidden">Edit</span>
                        </button>
                        <button
                          onClick={(e) => openArchiveConfirm(family, e)}
                          className="flex items-center gap-1 px-2 py-1.5 sm:p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-red-100 hover:text-red-600 text-xs sm:text-sm"
                          title="Archive family"
                          data-testid={`archive-family-${family.family_id}`}
                        >
                          <FaArchive />
                          <span className="sm:hidden">Archive</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
                
                <div className={`flex items-start justify-between mb-4 ${family.is_archived ? 'mt-4' : ''}`}>
                  <div className="flex-1 pr-16">
                    <h3 className={`text-xl font-bold mb-1 ${family.is_archived ? 'text-gray-500' : 'text-gray-800'}`}>
                      {family.name}
                    </h3>
                    {family.description && (
                      <p className={`text-sm ${family.is_archived ? 'text-gray-400' : 'text-gray-600'}`}>
                        {family.description}
                      </p>
                    )}
                  </div>
                  <FaCrown className={`text-xl ${family.is_archived ? 'text-gray-300' : 'text-yellow-500'}`} />
                </div>

                <div className={`flex items-center justify-between text-sm mb-4 ${family.is_archived ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center space-x-1">
                    <FaUsers />
                    <span>{family.members?.length || 0} {(family.members?.length || 0) === 1 ? 'member' : 'members'}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FaCalendarAlt />
                    <span>Events</span>
                  </div>
                </div>

                {family.is_archived ? (
                  <div className="flex items-center justify-center text-amber-600 font-medium text-sm">
                    <span>Hover to restore or delete</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-end text-blue-600 font-medium">
                    <span>View Details</span>
                    <FaArrowRight className="ml-2" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Incentives & Rewards Summary */}
        <div className="mt-10">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Your Rewards & Incentives</h3>
          <IncentivesSummary />
        </div>
      </main>

      {/* Create Family Modal */}
      {showCreateFamily && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" data-testid="create-family-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Create New Family</h3>
            
            <form onSubmit={handleCreateFamily} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Family Name</label>
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="e.g., The Smith Family"
                  required
                  data-testid="family-name-input"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={familyDescription}
                  onChange={(e) => setFamilyDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-y"
                  placeholder="A brief description of your family, your values, traditions, or anything special about your clan..."
                  rows="4"
                  data-testid="family-description-input"
                />
                <p className="text-xs text-gray-500 mt-1">This will be shown on your family page</p>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateFamily(false);
                    setFamilyName('');
                    setFamilyDescription('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  data-testid="cancel-create-family-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all"
                  style={ancestralColors ? {
                    background: `linear-gradient(135deg, ${ancestralColors.primary} 0%, ${ancestralColors.secondary} 100%)`
                  } : {
                    backgroundColor: '#2563EB'
                  }}
                  data-testid="submit-create-family-btn"
                >
                  {creating ? 'Creating...' : 'Create Family'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Family Modal */}
      {showEditFamily && editingFamily && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" data-testid="edit-family-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Edit Family</h3>
            
            <form onSubmit={handleEditFamily} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Family Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="edit-family-name-input"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-y"
                  rows="4"
                  data-testid="edit-family-description-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditFamily(false);
                    setEditingFamily(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  data-testid="submit-edit-family-btn"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && archivingFamily && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="archive-family-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Archive Family?</h3>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to archive <strong>{archivingFamily.name}</strong>? 
              The family will be hidden from your dashboard but can be restored later.
            </p>
            
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setArchivingFamily(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveFamily}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                data-testid="confirm-archive-family-btn"
              >
                Archive Family
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Permanent Delete Confirmation Modal */}
      {showDeleteConfirm && deletingFamily && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="delete-family-modal">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <FaTrash className="text-red-600 text-xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Delete Forever?</h3>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700 font-medium mb-2">⚠️ This action cannot be undone!</p>
              <p className="text-red-600 text-sm">
                This will permanently delete <strong>{deletingFamily.name}</strong> and ALL associated data including:
              </p>
              <ul className="text-red-600 text-sm mt-2 list-disc list-inside">
                <li>All events and RSVPs</li>
                <li>All photos and albums</li>
                <li>All stories, prayers, and gratitudes</li>
                <li>All dues records and transactions</li>
                <li>All chat messages</li>
                <li>All vault documents</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">
                Type <strong className="text-red-600">{deletingFamily.name}</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Type family name here"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingFamily(null);
                  setDeleteConfirmText('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePermanently}
                disabled={deleteConfirmText !== deletingFamily.name}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-family-btn"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Celebration Modal */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-md text-center transform animate-bounce-in pointer-events-auto">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-2">
              Congratulations!
            </h2>
            <p className="text-xl text-gray-700 mb-2">
              Welcome to <span className="font-bold text-purple-600">{newFamilyName}</span>!
            </p>
            <p className="text-gray-500 mb-4">
              Your family's legacy journey begins now. Let's build something beautiful together!
            </p>
            <button
              onClick={() => setShowCelebration(false)}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
            >
              Let's Go!
            </button>
          </div>
        </div>
      )}
      
      {/* Tutorial */}
      <Tutorial 
        context="dashboard" 
        run={runTutorial} 
        onComplete={dismissTutorial}
      />
      
      {/* Invite Professional Modal */}
      <InviteProfessionalModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => setShowInviteModal(false)}
      />
      
      {/* Refer Family Modal */}
      <ReferFamilyModal
        isOpen={showReferModal}
        onClose={() => setShowReferModal(false)}
        families={families.filter(f => !f.is_archived)}
      />
    </div>
  );
}

export default Dashboard;
